import {
  Box,
  FieldLabel,
  FlexBox,
  Icon,
  InputSelectionList,
  SelectionList,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { ArticleDataContext } from 'articleEditor/contexts/articleDataProvider';
import { useIsTranslating } from 'articleEditor/hooks/translations/useIsTranslating';
import useArticleTagsUpdate from 'articleEditor/hooks/useArticleTagsUpdate';
import { useSearchFetch } from 'hooks/useSearchFetch';
import { useDebounce } from 'lib-knowledge-delivery-common/hooks/useDebounce';
import { Tag } from 'lib-knowledge-management-common/graphql';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { useContext, useEffect, useState } from 'react';
import { Waypoint } from 'react-waypoint';

import s from './settings.module.scss';

type TagsProps = {
  tags: Array<Tag>;
  articleId: string;
};

export function Tags({ tags, articleId }: TagsProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const search = useDebounce(searchValue, 500);
  const [updateTags, updateTagsMeta] = useArticleTagsUpdate();
  const updateTagsError = updateTagsMeta.error;
  const { errorNotify } = useNotification();
  const isTranslating = useIsTranslating();

  const articleData = useContext(ArticleDataContext);

  const skip = articleData?.isSyncedWithElevio || !isOpen;
  const {
    data: tagsData,
    hasNextPage,
    isFetching,
    nextPage,
    error,
  } = useSearchFetch(
    {
      scope: 'tags',
      query: search,
      sortBy: { field: 'name', order: 'asc' },
    },
    { skip },
  );

  useEffect(() => {
    if (!error) return;
    errorNotify({
      text: 'Fetching available tags failed',
    });
  }, [error, errorNotify]);

  useEffect(() => {
    if (!updateTagsError) return;
    errorNotify({
      text: 'Updating tags failed.',
    });
  }, [updateTagsError, errorNotify]);

  return (
    <FlexBox alignItems="start" gap="16px">
      <FieldLabel className={s['settingsLabel']}>
        <Icon className={s['settingsIcon']} name="tag" />
        <Text.Paragraph variant="2" subdued>
          Tags
        </Text.Paragraph>
      </FieldLabel>

      <InputSelectionList<Tag>
        value={tags}
        rootClassName={s.tagsSelectionList}
        dropdownProps={{ className: s.tagsSelectionDropdown }}
        onToggleDropdown={setIsOpen}
        equalityFn={(a, b) => a.id === b.id}
        onChange={({ selectedItem }) => {
          if (!selectedItem) {
            return;
          }

          const tagsToRemove = [];
          const tagsToAdd = [];
          if (selectedItem.isSelected) {
            tagsToAdd.push(selectedItem.value);
          } else {
            tagsToRemove.push(selectedItem.value);
          }

          updateTags({ articleId, tagsToRemove, tagsToAdd });
        }}
        getItemTitleByValue={(tag) => tag?.name ?? ''}
        disabled={articleData?.isSyncedWithElevio || isTranslating}
        filter={{
          name: 'searchTagsInput',
          value: searchValue,
          onChange: (e) => {
            if (e.target.value === searchValue) {
              return;
            }

            setSearchValue(e.target.value);
          },
        }}
        multiple
        placeholder={
          articleData?.isSyncedWithElevio
            ? 'Detach article to add tags...'
            : 'Add tags...'
        }
      >
        {tagsData?.map((tag) => (
          <SelectionList.Item
            key={tag.id}
            value={tag}
            noFixedSelectionStateSpace
          >
            <FlexBox className={s.tagItem}>
              {tag.color && (
                <Box className={s.tagColor} background={tag.color} />
              )}
              <Text.Paragraph variant="2">{tag.name}</Text.Paragraph>
            </FlexBox>
          </SelectionList.Item>
        ))}

        {isFetching && (
          <FlexBox
            alignItems="center"
            justifyContent="center"
            padding="400 200"
          >
            <Spinner size={24} />
          </FlexBox>
        )}

        {!isFetching && !error && hasNextPage && (
          <Waypoint onEnter={nextPage} />
        )}
      </InputSelectionList>
    </FlexBox>
  );
}
